import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { House, Clock, SquareLibrary, UserRound, LayoutGrid, Users, Trash2, ShieldAlert } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { moveItemsInto } from '../moveItemsInto'
import { useToast, useUndo, PDAppIcon, type GlyphIcon } from '@papan/core-ui/ui'
import { isPapanDrag, readPapanDrag } from '@papan/core-ui/ui/papanDrag'
import { getActiveTenantId } from '@papan/api-client'
import { useDriveNavTargets } from '@papan/files/client/queries'

// Same custom MIME the drive DnD layer stamps (features/files/client/dnd.ts)

export type DriveShellPage = 'home' | 'recent' | 'drive' | 'projects' | 'pdapps' | 'shared' | 'search' | 'bin' | 'quarantine'

/**
 * The drive "app" shell — Google-Drive-style left navigation + a hero
 * content card, floating on the ambient stage (wrap the route element in a
 * `ws-clear` ThemedArea so the stage shows through; see DESIGN_DOC
 * §1). Home / Library / My Space (orgs) / Projects are the primary pages; Bin
 * and Quarantine live at the bottom of the panel — they moved here from the
 * taskbar so the taskbar stays app-level (drive is one app; future epic pins more).
 */
export function DriveShell({ page, urlPrefix, tenantName, isOrg, orgEmail, onBinDrop, children }: {
  page: DriveShellPage
  urlPrefix: string
  tenantName?: string
  /** Org context — gates the "My Space" (Persons/<email>) destination. */
  isOrg?: boolean
  orgEmail?: string | null
  /** Drop-to-delete on the Bin nav item (replaces the old taskbar bin target). */
  onBinDrop?: (files: { id: string; name: string; isFolder: boolean }[]) => void
  children: ReactNode
}) {
  const { t } = useTranslation('files')
  const navigate = useNavigate()
  const toast = useToast()
  const undo = useUndo()
  const [binDropActive, setBinDropActive] = useState(false)
  const [moveDropId, setMoveDropId] = useState<string | null>(null)

  // Library / My Space / Shared are id-addressed folders resolved from the
  // (cached) root listing + a path lookup. Each highlights only AT its folder;
  // deeper navigation inside falls back to no highlight like any other folder.
  const { folderId } = useParams<{ folderId?: string }>()
  const { libraryId, mySpaceId } = useDriveNavTargets({ isOrg, orgEmail })
  const onFolder = (id: string | null) => !!id && page === 'drive' && folderId === id

  // Folder-addressed nav items (Library, My Space) double as MOVE drop
  // targets — dragging drive rows (or Shared-page cards) onto them moves the
  // items into that folder. Realtime invalidation refreshes whichever list
  // view is open; no imperative refresh here.
  const tenantId = getActiveTenantId()
  const moveDropExtra = (targetId: string | null) => {
    if (!targetId) return undefined
    return {
      onDragEnter: (e: React.DragEvent) => {
        if (isPapanDrag(e.dataTransfer)) { e.preventDefault(); setMoveDropId(targetId) }
      },
      onDragOver: (e: React.DragEvent) => {
        if (isPapanDrag(e.dataTransfer)) { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }
      },
      // `dragleave` also fires when the pointer crosses onto the button's own
      // icon/label children, which strobed the highlight. Only clear when the
      // pointer actually left the button subtree.
      onDragLeave: (e: React.DragEvent) => {
        const next = e.relatedTarget as Node | null
        if (next && (e.currentTarget as HTMLElement).contains(next)) return
        setMoveDropId(prev => (prev === targetId ? null : prev))
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault()
        setMoveDropId(null)
        const transfer = readPapanDrag(e.dataTransfer)
        if (!transfer) return
        // Same guard every DriveView drop handler applies: a drag originating
        // in another tenant's tab names foreign ids, and moveFile would just
        // fail per-item with an opaque error. Shared-page cards stamp
        // `tenantId` precisely so receivers can check it.
        if (transfer.tenantId && transfer.tenantId !== tenantId) {
          toast.error(t('driveView.toast.crossTenantMove', {
            defaultValue: 'Items can’t be moved from one of your drives to another. Copy them instead — moving into or out of a shared folder still works.',
          }))
          return
        }
        const files = transfer.files.filter(f => f.id !== targetId)
        if (files.length === 0) return
        // Any drive row can land here, so a #801 split is possible: the helper reports what
        // the server did (split / copy / refusal) and records the split for undo.
        void moveItemsInto(files, targetId, { t, toast, undo })
      },
      moveHighlight: moveDropId === targetId,
    }
  }

  const items: Array<{ id: DriveShellPage; testId?: string; icon: GlyphIcon; label: string; to: string; active: boolean; dropTargetId?: string | null }> = [
    { id: 'home', icon: House, label: t('driveHome.nav.home'), to: `${urlPrefix}/home`, active: page === 'home' },
    // #653 — recent changes, grouped.
    { id: 'recent', icon: Clock, label: t('driveHome.nav.recent'), to: `${urlPrefix}/recent`, active: page === 'recent' },
    { id: 'drive', testId: 'library', icon: SquareLibrary, label: t('driveHome.nav.library'), to: libraryId ? `${urlPrefix}/folder/${libraryId}` : `${urlPrefix}/folder`, active: onFolder(libraryId), dropTargetId: libraryId },
    ...(mySpaceId ? [{
      id: 'drive' as DriveShellPage,
      testId: 'my-space',
      icon: UserRound,
      label: t('driveHome.nav.mySpace'),
      to: `${urlPrefix}/folder/${mySpaceId}`,
      active: onFolder(mySpaceId),
      dropTargetId: mySpaceId,
    }] : []),
    { id: 'projects', icon: LayoutGrid, label: t('driveHome.nav.projects'), to: `${urlPrefix}/projects`, active: page === 'projects' },
    { id: 'pdapps', testId: 'pdapps', icon: PDAppIcon, label: t('driveHome.nav.miniapps'), to: `${urlPrefix}/pdapps`, active: page === 'pdapps' },
    // The Shared PAGE (incoming/outgoing overview).
    { id: 'shared', testId: 'shared', icon: Users, label: t('driveHome.nav.shared'), to: `${urlPrefix}/shared`, active: page === 'shared' },
  ]

  const renderItem = (item: { id: DriveShellPage; testId?: string; icon: GlyphIcon; label: string; to: string; active: boolean }, extra?: {
    onDragEnter?: (e: React.DragEvent) => void
    onDragOver?: (e: React.DragEvent) => void
    onDragLeave?: (e: React.DragEvent) => void
    onDrop?: (e: React.DragEvent) => void
    /** Bin (destructive) highlight — red. */
    highlight?: boolean
    /** Folder move-target highlight — accent, not danger. */
    moveHighlight?: boolean
  }) => (
    <button
      key={item.to}
      onClick={() => navigate(item.to)}
      data-active={item.active || undefined}
      data-testid={`drive-nav-${item.testId ?? item.id}`}
      className={`ws-snav-item ${extra?.highlight ? 'ring-2 ring-red-500 !bg-red-600/25' : ''} ${extra?.moveHighlight ? 'ring-2 ring-violet-400 !bg-violet-500/20' : ''}`}
      onDragEnter={extra?.onDragEnter}
      onDragOver={extra?.onDragOver}
      onDragLeave={extra?.onDragLeave}
      onDrop={extra?.onDrop}
    >
      <item.icon size={16} className="shrink-0 pointer-events-none" />
      <span className="truncate pointer-events-none">{item.label}</span>
    </button>
  )

  return (
    // The window body only — the drive top bar (account email + theme
    // toggle) now lives in the shell's ONE permanent top bar (App.tsx).
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5 gap-3">
      <nav className="ws-card w-52 shrink-0 flex flex-col py-3 px-2">
        <div className="px-3 pb-3 mb-1 border-b themed-border-secondary">
          <div className="text-sm font-semibold themed-text">{t('driveHome.nav.title')}</div>
          {tenantName && <div className="text-xs themed-text-muted truncate mt-0.5">{tenantName}</div>}
        </div>
        <div className="flex flex-col gap-0.5">
          {items.map(i => renderItem(i, moveDropExtra(i.dropTargetId ?? null)))}
        </div>
        <div className="flex-1" />
        <div className="flex flex-col gap-0.5 pt-2 border-t themed-border-secondary">
          {renderItem(
            { id: 'bin', icon: Trash2, label: t('driveHome.nav.bin'), to: `${urlPrefix}/bin`, active: page === 'bin' },
            onBinDrop ? {
              onDragEnter: (e) => {
                if (isPapanDrag(e.dataTransfer)) { e.preventDefault(); setBinDropActive(true) }
              },
              onDragOver: (e) => {
                if (isPapanDrag(e.dataTransfer)) { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }
              },
              onDragLeave: () => setBinDropActive(false),
              onDrop: (e) => {
                e.preventDefault()
                setBinDropActive(false)
                const transfer = readPapanDrag(e.dataTransfer)
                if (!transfer) return
                // Same guard as the folder targets above: another drive's items aren't ours to bin.
                if (transfer.tenantId && transfer.tenantId !== tenantId) {
                  toast.error(t('driveView.toast.crossTenantMove', {
                    defaultValue: 'Items can’t be moved from one of your drives to another. Copy them instead — moving into or out of a shared folder still works.',
                  }))
                  return
                }
                if (transfer.files.length > 0) onBinDrop(transfer.files)
              },
              highlight: binDropActive,
            } : undefined,
          )}
          {renderItem({ id: 'quarantine', icon: ShieldAlert, label: t('driveHome.nav.quarantine'), to: `${urlPrefix}/quarantine`, active: page === 'quarantine' })}
        </div>
      </nav>
      <main className="ws-card-hero flex-1 min-w-0 flex flex-col overflow-hidden">
        {children}
      </main>
      </div>
    </div>
  )
}
